import React, { useCallback, useEffect, useState } from "react";

import { getAdminUser, updateUser } from "services";

import { clearLocalKlimVar, incomplete, useCancelAsyncOnUnmount, useParams, useToggleNavs } from "utils";

import { adminRoles } from "enums";

import { AdminRolesDrawer, Button, Col, Form, Link, MainPage, Option, Row, Select, Typography } from "components";

import { AccountLogo } from "pages/app/account/components/AccountLogo";

export const AdminAccessConfirm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRequestConfirmed, setIsRequestConfirmed] = useState(false);
  const [user, setUser] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);

  const { id } = useParams();

  const signal = useCancelAsyncOnUnmount()();

  useToggleNavs(false, false);

  const loadUser = useCallback(() => {
    getAdminUser({
      signal,
      id,
    })
      .then((res) => {
        setUser(res);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [signal, id]);

  const complete = (v) => {
    setIsLoading(true);
    updateUser({
      ...v,
      ...{ signal, id },
    })
      .then((res) => {
        clearLocalKlimVar("token");

        if (res.success) {
          setIsRequestConfirmed(true);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    clearLocalKlimVar("token");
    loadUser();
  }, []); // eslint-disable-line

  const SignInForm = () => (
    <div>
      <Row>
        <Col>
          <Typography variant="paragraph-m" className="margin-bottom-0">
            <b>You have a responsibility to only grant people access when it's absolutely required.</b>{" "}
            <Link tracking={false} onClick={() => setShowDrawer(true)}>
              View admin role details
            </Link>
            <br />
            <br />
            All users who do not access admin for 3 months are automatically disabled and must request access again. Select the minimum required tier to grant{" "}
            <b>
              {user.firstName} {user.lastName}
            </b>{" "}
            access. You may only grant tier 1 access by signing in and modifying an existing user's role.
          </Typography>
        </Col>
      </Row>
      <Form name="signin" className="margin-top-300" onFinish={(v) => complete(v)} onFinishFailed={(v) => incomplete(v)}>
        <Row>
          <Col>
            <Select name="role" label="User role" rules={[{ required: true }]}>
              {adminRoles.map((role, i) => (
                <Option value={role.role} key={i} disabled={role.role === "tier1"}>
                  {role.label ?? role.role}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Item>
              <Button type="primary" htmlType="submit" disabled={isLoading} loading={isLoading}>
                Grant admin access
              </Button>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Item>
              <Button type="secondary" href="/sign-in" disabled={isLoading} loading={isLoading}>
                Back to sign in
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );

  return (
    <MainPage pageName={"sign-in-up"}>
      <Row className="mini-form">
        <Col>
          <AccountLogo />
          <Row>
            <Col>
              <Typography variant="h1">Admin access confirmation</Typography>
            </Col>
          </Row>
          {!isRequestConfirmed ? (
            <SignInForm />
          ) : (
            <Row>
              <Col>
                <Typography variant="paragraph-m" className="margin-bottom-0">
                  The user has been granted access and can now set their first password using the normal reset password functionality.
                </Typography>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      <AdminRolesDrawer visible={showDrawer} onClose={() => setShowDrawer(false)} />
    </MainPage>
  );
};
